import { CheckboxVisibility, DetailsListLayoutMode, MessageBarType, Selection, SelectionMode } from '@fluentui/react';
import {
  Button,
  buttonStylesDanger,
  buttonStylesPrimary,
  ConfirmDialog,
  DetailsList,
  MessageBar,
  useTheme,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EgressDeleteRequest } from '../../../../../admin-center/gen/fw_management/fw_management_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { messageBarContainerStyles } from '../../../common/common.styles';
import { buildUserContext, getDummyList } from '../../../common/utils';
import SkeletonLoader from '../../SkeletonLoader/SkeletonLoader';
import { actionAreaStyle, firewallManagementTableStyles, TableBackgroundStyle } from '../FWManagement.styles';
import { getMessageBarProps } from '../FWManagement.utils';

interface OutboundPortItem {
  outboundPort: string;
  outboundPortProtocol: string;
}

interface PortTableProps {
  fwPorts: string[];
  onSubmitSuccess: () => Promise<void>;
  isLoading: boolean;
  onAddButtonClick: () => void;
}

const dummyList = getDummyList<OutboundPortItem>(5);

export default function PortTable({ fwPorts, onSubmitSuccess, isLoading, onAddButtonClick }: PortTableProps) {
  const service = useAdminCenterService();
  const theme = useTheme();
  const user = useUser();
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [responseMessageType, setResponseMessageType] = useState<MessageBarType>(MessageBarType.info);
  const [showMessageBar, setShowMessagebar] = useState<boolean>(false);
  const [tableSelections, setTableSelections] = useState<OutboundPortItem[]>([]);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);
  const [isDeletionPending, setIsDeletionPending] = useState<boolean>(false);

  const selectionRef = useRef<Selection>(
    new Selection({
      onSelectionChanged: () => {
        const selectedItems = selectionRef.current.getSelection();
        handleSelectionChanged(selectedItems);
      },
      getKey: (item: any) => item.userEmail,
    })
  );

  const handleSelectionChanged = useCallback((items: any[]) => {
    setTableSelections(items);
  }, []);

  useEffect(() => {
    setTableSelections([]);
  }, [isLoading]);

  const portData: OutboundPortItem[] = fwPorts.map((port) => {
    const [outboundPort, protocol] = port.split('-');
    return {
      outboundPort,
      outboundPortProtocol: protocol ? protocol.toUpperCase() : '-',
    };
  });

  const handleDelete = async () => {
    setIsDeletionPending(true);
    try {
      const userContext = await buildUserContext(user);
      const ports = tableSelections.map((item) => `${item.outboundPort}-${item.outboundPortProtocol.toLowerCase()}`);
      const request: EgressDeleteRequest = {
        deletedFwPorts: ports,
        modifiedBy: user?.name ?? '',
        timestamp: Date.now() / 1000,
        userContext: userContext,
      };
      const response = await service.deleteEgressRules(request);

      if (response.statusUpdate) {
        const { type, message } = getMessageBarProps(response.statusUpdate);
        setResponseMessage(message);
        setResponseMessageType(type);
      } else {
        setResponseMessage('Failed to Delete');
        setResponseMessageType(MessageBarType.error);
      }
      await onSubmitSuccess();
    } catch (error) {
      setResponseMessage(`Error: ${error}`);
      setResponseMessageType(MessageBarType.error);
    } finally {
      setShowMessagebar(true);
      setIsDeletionPending(false);
      setShowDeleteConfirmationDialog(false);
      setTableSelections([]);
    }
  };

  return (
    <>
      <div style={TableBackgroundStyle(theme)}>
        {showMessageBar && (
          <div style={messageBarContainerStyles}>
            <MessageBar timeout={6000} onDismiss={() => setShowMessagebar(false)} messageBarType={responseMessageType}>
              {responseMessage}
            </MessageBar>
          </div>
        )}
        <div style={actionAreaStyle}>
          <Button styles={buttonStylesPrimary} text="Add" onClick={onAddButtonClick} />
          <Button
            styles={buttonStylesDanger}
            text="Delete"
            onClick={() => {
              setShowDeleteConfirmationDialog(true);
            }}
            disabled={tableSelections.length === 0 || isLoading}
          />
        </div>
        {!isLoading && portData.length === 0 ? (
          <MessageBar messageBarType={MessageBarType.warning}>{'No data available'}</MessageBar>
        ) : (
          <DetailsList
            selection={selectionRef.current}
            selectionPreservedOnEmptyClick={true}
            selectionMode={SelectionMode.multiple}
            checkboxVisibility={CheckboxVisibility.always}
            layoutMode={DetailsListLayoutMode.fixedColumns}
            columns={[
              {
                key: 'outboundPort',
                fieldName: 'outboundPort',
                name: 'Ports',
                minWidth: 200,
                onRender: (item: OutboundPortItem) => (
                  <>{!isLoading ? item.outboundPort : <SkeletonLoader width="65%" height="50%" />}</>
                ),
              },
              {
                key: 'outboundPortProtocol',
                fieldName: 'outboundPortProtocol',
                name: 'Protocol',
                minWidth: 200,
                onRender: (item: OutboundPortItem) => (
                  <>{!isLoading ? item.outboundPortProtocol : <SkeletonLoader width="65%" height="50%" />}</>
                ),
              },
            ]}
            styles={firewallManagementTableStyles(theme)}
            items={isLoading ? dummyList : portData}
            setKey="outboundPort"
          />
        )}
      </div>
      <ConfirmDialog
        hidden={!showDeleteConfirmationDialog}
        title="Confirm Delete"
        msg={
          isDeletionPending
            ? 'Deleting Ports...'
            : tableSelections.length === 1
            ? 'Are you sure you want to delete the selected outbound port?'
            : `Are you sure you want to delete ${tableSelections.length} outbound ports?`
        }
        confirmationButtonText={isDeletionPending ? '...' : 'Yes'}
        dismissalButtonText="Cancel"
        onConfirm={handleDelete}
        onDismiss={() => {
          setShowDeleteConfirmationDialog(false);
        }}
        confirmationButtonDisabled={isDeletionPending}
      />
    </>
  );
}
